import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from '../../assets/styles/report/AdAnalysis.module.scss';
import ReportLayout from '../../components/Layout/ReportLayout';
import { useReportFilter } from '../../contexts/ReportContext';
import { useProjectContext } from "../../contexts/ProjectContext";

// ============================================================================
// Types
// ============================================================================

// バックエンドから受け取る Metric 型 (値 + 変動率 + トレンド)
interface MetricValue {
  value: number;
  pct: number;
  trend: 'up' | 'down' | 'flat';
}

// ============================================================================
// Sub Components (Charts)
// ============================================================================

const LineChart: React.FC<{ data: any[], color: string }> = ({ data, color }) => {
  if (!data || data.length === 0) return <div className={styles.noData}>No Data</div>;

  const maxVal = Math.max(...data.map(d => d.spend)) || 1;
  const width = 400;
  const height = 150;
  const padding = 20;

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * (width - padding * 2) + padding;
    const y = height - ((d.spend / maxVal) * (height - padding * 2) + padding);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className={styles.svgChart}>
      <defs>
        <linearGradient id={`chartGradient-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.5, ease: "easeInOut" }}
        d={`M ${points}`}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <motion.path
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 1 }}
        d={`M ${points} L ${width - padding},${height} L ${padding},${height} Z`}
        fill={`url(#chartGradient-${color})`}
      />
      {data.map((d, i) => {
        const x = (i / (data.length - 1)) * (width - padding * 2) + padding;
        const y = height - ((d.spend / maxVal) * (height - padding * 2) + padding);
        return (
          <motion.circle
            key={i}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1 + i * 0.1 }}
            cx={x}
            cy={y}
            r="4"
            fill="#fff"
            stroke={color}
            strokeWidth="2"
          />
        );
      })}
    </svg>
  );
};

const BubbleChart: React.FC<{ data: any[] }> = ({ data }) => {
  if (!data || data.length === 0) return <div className={styles.noData}>No Data</div>;

  const width = 600;
  const height = 300;
  const padding = 60;

  const maxSessions = Math.max(...data.map(d => d.sessions)) || 1;
  const maxTime = Math.max(...data.map(d => d.time)) || 1;

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className={styles.svgChart}>
      <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#333" strokeWidth="1" />
      <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#333" strokeWidth="1" />

      <text x={width / 2} y={height - 15} textAnchor="middle" fill="#666" fontSize="10">セッション数 (流入量)</text>
      <text x={15} y={height / 2} textAnchor="middle" fill="#666" fontSize="10" transform={`rotate(-90, 15, ${height / 2})`}>平均エンゲージメント時間 (質)</text>

      {data.map((d, i) => {
        const cx = padding + (d.sessions / maxSessions) * (width - padding * 2);
        const cy = height - padding - (d.time / maxTime) * (height - padding * 2);
        const r = Math.sqrt(d.sessions) * 0.5 + 8;
        const color = `hsl(${200 + i * 30}, 80%, 65%)`;

        return (
          <g key={i}>
            <motion.circle
              initial={{ r: 0, opacity: 0 }}
              animate={{ r, opacity: 0.6 }}
              transition={{ delay: i * 0.1, duration: 0.8 }}
              cx={cx}
              cy={cy}
              r={Math.sqrt(d.sessions) * 0.5 + 8}
              fill={color}
            />
            <motion.text
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 + i * 0.1 }}
              x={cx}
              y={cy - (Math.sqrt(d.sessions) * 0.5 + 8) - 10}
              textAnchor="middle"
              fill="#fff"
              fontSize="10"
              fontWeight="800"
            >
              {d.name.split(' / ')[0]}
            </motion.text>
            <motion.text
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 + i * 0.1 }}
              x={cx}
              y={cy + 4}
              textAnchor="middle"
              fill="#fff"
              fontSize="8"
              fontWeight="600"
            >
              {d.sessions.toLocaleString()} / {d.time}s
            </motion.text>
          </g>
        );
      })}
    </svg>
  );
};

const MonthlyComboChart: React.FC<{ data: any[] }> = ({ data }) => {
  if (!data || data.length === 0) return <div className={styles.noData}>No Data</div>;

  const width = 600;
  const height = 300;
  const padding = 50;

  const maxCost = Math.max(...data.map(d => d.cost)) || 1;
  const maxCv = Math.max(...data.map(d => d.cv)) || 1;
  const barWidth = (width - padding * 2) / data.length * 0.5;
  const baseline = height - padding;

  const linePoints = data.map((d, i) => {
    const x = padding + (i / (data.length - 1)) * (width - padding * 2);
    const y = height - padding - (d.cv / maxCv) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className={styles.svgChart}>
      {/* Bars (Cost) */}
      {data.map((d, i) => {
        const barHeight = (d.cost / maxCost) * (height - padding * 2);
        const x = padding + (i / (data.length - 1)) * (width - padding * 2) - barWidth / 2;
        const y = baseline - barHeight;
        return (
          <g key={i}>
            <motion.rect
              initial={{ height: 0, y: baseline }}
              animate={{ height: barHeight, y }}
              transition={{ delay: i * 0.05, duration: 0.8 }}
              x={x}
              y={y}
              width={barWidth}
              fill="rgba(255, 255, 255, 0.08)"
              stroke="rgba(255, 255, 255, 0.2)"
              rx="4"
            />
            <text x={x + barWidth / 2} y={y - 8} textAnchor="middle" fill="#888" fontSize="9">
              ¥{Math.round(d.cost / 10000)}万
            </text>
          </g>
        );
      })}
      {/* Line (CV) */}
      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.5, ease: "easeInOut" }}
        d={`M ${linePoints}`}
        fill="none"
        stroke="#00c6ff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Points (CV) */}
      {data.map((d, i) => {
        const x = padding + (i / (data.length - 1)) * (width - padding * 2);
        const y = height - padding - (d.cv / maxCv) * (height - padding * 2);
        return (
          <g key={i}>
            <motion.circle
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1 + i * 0.1 }}
              cx={x} cy={y} r="4" fill="#00c6ff" stroke="#0a0a0a" strokeWidth="2"
            />
            <text x={x} y={y - 12} textAnchor="middle" fill="#00c6ff" fontSize="11" fontWeight="800">
              {d.cv}
            </text>
          </g>
        );
      })}
      {/* Labels */}
      {data.map((d, i) => (
        <text key={i} x={padding + (i / (data.length - 1)) * (width - padding * 2)} y={baseline + 20} textAnchor="middle" fill="#666" fontSize="10">
          {d.month}
        </text>
      ))}
    </svg>
  );
};

const AreaBarChart: React.FC<{ data: any[] }> = ({ data }) => {
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>データがありません</div>;

  const width = 600;
  const height = 300;
  const padding = 50;
  const maxVal = Math.max(...data.map(d => d.cost)) || 1;
  const barWidth = (width - padding * 2) / data.length * 0.5;
  const baseline = height - padding;

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className={styles.svgChart}>
      {data.map((d, i) => {
        const barHeight = (d.cost / maxVal) * (height - padding * 2);
        const x = padding + (i / (data.length - 1)) * (width - padding * 2) - barWidth / 2;
        const y = baseline - barHeight;
        return (
          <g key={i}>
            <motion.rect
              initial={{ height: 0, y: baseline }}
              animate={{ height: barHeight, y }}
              transition={{ delay: i * 0.1, duration: 1 }}
              x={x} y={y} width={barWidth}
              fill="rgba(0, 198, 255, 0.4)" stroke="#00c6ff" strokeWidth="1.5" rx="6"
            />
            <text x={x + barWidth / 2} y={baseline + 22} textAnchor="middle" fill="#999" fontSize="12" fontWeight="700">
              {d.area}
            </text>
            <text x={x + barWidth / 2} y={y - 12} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="800">
              {d.cv}CV / ¥{Math.round(d.cost / 10000)}万
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const Sparkline: React.FC<{ data: number[], color: string }> = ({ data, color }) => {
  if (!data || data.length === 0) return null;
  const height = 30;
  const width = 80;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={width} cy={height - ((data[data.length - 1] - min) / range) * height} r="3" fill={color} />
    </svg>
  );
};

// ============================================================================
// Helper for MoM display
// ============================================================================
// ★ Modified to use backend-calculated pct and trend
const MomDiff: React.FC<{ data: MetricValue | undefined, format?: (v: number) => string, inverse?: boolean }> = ({ data, format, inverse = false }) => {
  if (!data) return <span style={{ fontSize: '10px', color: '#888' }}>-</span>;

  const { pct, trend } = data;
  const isUp = trend === 'up';
  const isGood = inverse ? !isUp : isUp;

  const color = trend === 'flat' ? '#888' : isGood ? '#00f2ff' : '#ff0055';
  const icon = trend === 'flat' ? '-' : isUp ? '↑' : '↓';

  return (
    <div className={styles.momDiff} style={{ color }}>
      <span>{icon} {pct}%</span>
    </div>
  );
};

// ============================================================================
// Main Report Component
// ============================================================================

const AdAnalysis: React.FC = () => {
  const { selectedProjectId } = useProjectContext();
  const { selectedPeriod, customStart, customEnd } = useReportFilter();

  // Data States
  const [summaryData, setSummaryData] = useState<any>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [keywordData, setKeywordData] = useState<any[]>([]);

  // Drill-down States
  const [platformDetail, setPlatformDetail] = useState<any[]>([]);
  const [selectedPlatformKey, setSelectedPlatformKey] = useState<string | null>(null);

  // UI States
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isOverallModalOpen, setIsOverallModalOpen] = useState(false);

  // ==========================================================================
  // Fetch Logic
  // ==========================================================================

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedProjectId) return;
      setIsLoading(true);
      try {
        const params: Record<string, string> = {
          project_id: String(selectedProjectId),
          period: selectedPeriod,
        };
        if (selectedPeriod === 'custom' && customStart && customEnd) {
          params.start = customStart;
          params.end = customEnd;
        }
        const queryParams = new URLSearchParams(params);

        // Fetch 3 APIs in parallel
        const [sumRes, chartRes, kwRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/get-ad-analysis-summary?${queryParams}`, { credentials: 'include' }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/get-ad-analysis-charts?${queryParams}`, { credentials: 'include' }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/get-ad-analysis-keywords?project_id=${selectedProjectId}&limit=5`, { credentials: 'include' })
        ]);

        if (sumRes.ok) setSummaryData(await sumRes.json());
        if (chartRes.ok) setChartData(await chartRes.json());
        if (kwRes.ok) setKeywordData(await kwRes.json());

      } catch (error) {
        console.error("AdAnalysis Fetch Error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedProjectId, selectedPeriod, customStart, customEnd]);

  // Drill-down Fetch
  const handleCardClick = async (platformKey: string) => {
    if (selectedPlatformKey === platformKey) {
      setSelectedPlatformKey(null);
      return;
    }

    setSelectedPlatformKey(platformKey);
    setIsDetailLoading(true);
    setPlatformDetail([]);

    try {
      const params: Record<string, string> = {
        project_id: String(selectedProjectId),
        platform_key: platformKey,
        period: selectedPeriod,
      };
      if (selectedPeriod === 'custom' && customStart && customEnd) {
        params.start = customStart;
        params.end = customEnd;
      }
      const queryParams = new URLSearchParams(params);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/get-ad-analysis-details?${queryParams.toString()}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        setPlatformDetail(await response.json());
      }
    } catch (error) {
      console.error("Detail Fetch Error:", error);
    } finally {
      setIsDetailLoading(false);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================

  if (isLoading || !summaryData || !chartData) {
    return <ReportLayout><div className={styles.loading}>読み込み中...</div></ReportLayout>;
  }

  // Extract Data
  const { overall = {}, adPerformance = [], overallMonthlyData = [] } = summaryData;
  const { mediaEngagement = [], monthlyTrends = [], areaPerformance = [] } = chartData;

  const selectedPlatformData = adPerformance.find((a: any) => (a.platformKey || a.platform) === selectedPlatformKey);
  const currentPlatformColor = selectedPlatformData?.color || '#00c6ff';

  return (
    <ReportLayout>
      <div className={styles.reportContent}>
        <div className={styles.scrollArea}>

          {/* 1. Overall Performance Card */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => setIsOverallModalOpen(true)}
            className={styles.overallCard}
          >
            <div className={styles.overallCardBody}>
              <div className={styles.overallStatItem}>
                <div className={`${styles.overallLabel} ${styles.primary}`}>総費用</div>
                <div className={`${styles.overallValue} ${styles.primary}`}>
                  ¥{overall.totalSpend?.value?.toLocaleString() ?? '0'}
                </div>
                <MomDiff data={overall.totalSpend} />
              </div>
              <div className={styles.overallDivider}></div>
              <div className={styles.overallStatItem}>
                <div className={styles.overallLabel}>表示回数</div>
                <div className={styles.overallValue}>
                  {overall.totalImpressions?.value?.toLocaleString() ?? '0'}
                </div>
                <MomDiff data={overall.totalImpressions} />
              </div>
              <div className={styles.overallStatItem}>
                <div className={styles.overallLabel}>クリック数</div>
                <div className={styles.overallValue}>
                  {overall.totalClicks?.value?.toLocaleString() ?? '0'}
                </div>
                <MomDiff data={overall.totalClicks} />
              </div>
              <div className={styles.overallStatItem}>
                <div className={styles.overallLabel}>CV</div>
                <div className={styles.overallValue}>
                  {overall.totalCv?.value?.toLocaleString() ?? '0'}
                </div>
                <MomDiff data={overall.totalCv} />
              </div>
              <div className={styles.overallStatItem}>
                <div className={styles.overallLabel}>CPA</div>
                <div className={styles.overallValue}>
                  ¥{overall.totalCpa?.value?.toLocaleString() ?? '0'}
                </div>
                <MomDiff data={overall.totalCpa} inverse />
              </div>
            </div>

            <div className={styles.cardFooter}>
              <span>
                詳細・月次推移を見る <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
              </span>
            </div>
          </motion.div>

          {/* 2. Ad Performance Cards */}
          <section className={styles.sectionAdPerformance}>
            <div className={styles.cardGrid}>
              {adPerformance.map((ad: any, idx: number) => {
                const trendData = ad.monthlyCvTrend || [];
                const isActive = ad.active !== false;

                return (
                  <div
                    key={idx}
                    className={`${styles.adCard} ${selectedPlatformKey === (ad.platformKey || ad.platform) ? styles.active : ''}`}
                    onClick={() => isActive && handleCardClick(ad.platformKey || ad.platform)}
                    style={{ opacity: isActive ? 1 : 0.6, cursor: isActive ? 'pointer' : 'default' }}
                  >
                    <div className={styles.adCardHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className={styles.platformName}>{ad.platform}</span>
                        {!isActive && <span style={{ fontSize: '10px', background: '#333', padding: '2px 6px', borderRadius: '4px', color: '#888' }}>non-active</span>}
                      </div>
                      <div className={styles.indicatorWrap} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        {isActive && trendData.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <Sparkline data={trendData} color={ad.color} />
                          </div>
                        )}
                        <div className={styles.indicatorCircle} style={{ '--accent-color': isActive ? ad.color : '#333' } as any}>
                          <span className={styles.indicatorValue}>
                            {ad.conversions?.value ?? '-'}<small>CV</small>
                            {isActive && <MomDiff data={ad.conversions} />}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={styles.metricsList}>
                      <div className={styles.metricItem}>
                        <span className={styles.metricLabel}>利用金額</span>
                        <span className={styles.metricValue}>
                          {isActive ? `¥${ad.spend?.value?.toLocaleString()}` : '-'}
                        </span>
                        {isActive && <MomDiff data={ad.spend} />}
                      </div>
                      <div className={styles.metricItem}>
                        <span className={styles.metricLabel}>表示回数</span>
                        <span className={styles.metricValue}>
                          {isActive ? ad.impressions?.value?.toLocaleString() : '-'}
                        </span>
                        {isActive && <MomDiff data={ad.impressions} />}
                      </div>
                      <div className={styles.metricItem}>
                        <span className={styles.metricLabel}>クリック数</span>
                        <span className={styles.metricValue}>
                          {isActive ? ad.clicks?.value?.toLocaleString() : '-'}
                        </span>
                        {isActive && <MomDiff data={ad.clicks} />}
                      </div>
                      <div className={styles.metricItem}>
                        <span className={styles.metricLabel}>CPC</span>
                        <span className={styles.metricValue}>
                          {isActive ? `¥${ad.cpc?.value}` : '-'}
                        </span>
                        {isActive && <MomDiff data={ad.cpc} inverse />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* 3. Trend Analysis */}
          <section className={styles.sectionTrendAnalysis}>
            <div className={styles.analysisGrid}>
              <div className={styles.analysisCard}>
                <h3>メディア別エンゲージメント分析</h3>
                <p className={styles.sectionDesc}>流入量（セッション数）と滞在時間（質）の関係です。</p>
                <div className={styles.chartWrap}>
                  <BubbleChart data={mediaEngagement} />
                </div>
              </div>
              <div className={styles.analysisCard}>
                <h3>月次実績推移</h3>
                <p className={styles.sectionDesc}>過去6ヶ月のコストとCV数の推移です。</p>
                <div className={styles.chartWrap}>
                  <MonthlyComboChart data={monthlyTrends} />
                </div>
              </div>
            </div>
          </section>

          {/* 4. Area & Keywords */}
          <section className={styles.sectionAreaKeyword}>
            <div className={styles.analysisGrid}>
              <div className={styles.analysisCard}>
                <h3>エリア別パフォーマンス</h3>
                <p className={styles.sectionDesc}>主要エリアごとのコスト・獲得数を比較します。</p>
                <div className={styles.chartWrap}>
                  <AreaBarChart data={areaPerformance} />
                </div>
              </div>
              <div className={styles.analysisCard}>
                <h3>高効率キーワードTOP5</h3>
                <p className={styles.sectionDesc}>獲得数が多くCPAが低い検索キーワードです。</p>
                <div className={styles.keywordListWrap}>
                  {keywordData.length === 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        color: '#666'
                      }}
                    >
                      データがありません
                    </div>
                  ) : (
                    <table className={styles.keywordTable}>
                      <thead>
                        <tr>
                          <th>キーワード</th>
                          <th>CV数</th>
                          <th>CPA</th>
                          <th>マッチタイプ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keywordData.map((k, i) => (
                          <tr key={i}>
                            <td className={styles.tdKeyword}>{k.keyword}</td>
                            <td className={styles.tdCv}>{k.cv}</td>
                            <td className={styles.tdCpa}>¥{k.cpa.toLocaleString()}</td>
                            <td><span className={styles.matchBadge}>{k.matchType}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* Side Drawers */}
      {/* ========================================================================= */}

      <AnimatePresence>
        {/* A. Platform Detail Drawer */}
        {selectedPlatformKey && (
          <>
            <motion.div
              className={styles.drawerBackdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedPlatformKey(null)}
            />
            <motion.div
              className={styles.sideDrawer}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              <div className={styles.drawerHeader}>
                <h2 className={styles.drawerTitle}>{selectedPlatformData?.platform || selectedPlatformKey} 統計詳細</h2>
                <button className={styles.drawerCloseBtn} onClick={() => setSelectedPlatformKey(null)}>×</button>
              </div>
              <div className={styles.drawerBody}>
                {isDetailLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>読み込み中...</div>
                ) : (
                  <>
                    <div className={styles.trendSection}>
                      <h3 className={styles.drawerSubTitle}>日別費用推移チャート</h3>
                      <div className={styles.chartContainer}>
                        <LineChart data={platformDetail} color={currentPlatformColor} />
                      </div>
                    </div>
                    <div className={styles.trendListSection}>
                      <h3 className={styles.drawerSubTitle}>デイリーレポート</h3>
                      <table className={styles.drawerTable}>
                        <thead>
                          <tr>
                            <th>日付</th>
                            <th>利用金額</th>
                            <th>表示回数</th>
                            <th>クリック</th>
                            <th>CPC</th>
                            <th>CV</th>
                          </tr>
                        </thead>
                        <tbody>
                          {platformDetail.map((d: any, i: number) => (
                            <tr key={i}>
                              <td>{d.date}</td>
                              <td>¥{d.spend.toLocaleString()}</td>
                              <td>{d.impressions?.toLocaleString() || 0}</td>
                              <td>{d.clicks?.toLocaleString() || 0}</td>
                              <td>¥{d.cpc || 0}</td>
                              <td className={styles.tdCv}>{d.cv}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}

        {/* B. Overall Summary Drawer */}
        {isOverallModalOpen && (
          <>
            <motion.div
              className={styles.drawerBackdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOverallModalOpen(false)}
            />
            <motion.div
              className={styles.sideDrawer}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={{ width: '800px', maxWidth: '90%' }}
            >
              <div className={styles.drawerHeader}>
                <h2 className={styles.drawerTitle}>総費用パフォーマンス詳細 (直近6ヶ月)</h2>
                <button className={styles.drawerCloseBtn} onClick={() => setIsOverallModalOpen(false)}>×</button>
              </div>
              <div className={styles.drawerBody}>
                <div className={styles.trendSection} style={{ marginBottom: '32px' }}>
                  <h3 className={styles.drawerSubTitle}>費用 vs CV 推移</h3>
                  <div style={{ height: '250px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '16px' }}>
                    <MonthlyComboChart data={overallMonthlyData.map((d: any) => ({ month: d.month, cost: d.cost, cv: d.cv }))} />
                  </div>
                </div>

                <div className={styles.trendListSection}>
                  <h3 className={styles.drawerSubTitle}>月次詳細データ</h3>
                  <table className={styles.drawerTable}>
                    <thead>
                      <tr>
                        <th>月</th>
                        <th>費用 (Cost)</th>
                        <th>表示回数 (Imp)</th>
                        <th>クリック (Click)</th>
                        <th>CV</th>
                        <th>CPA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overallMonthlyData.map((row: any, i: number) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 'bold' }}>{row.month}</td>
                          <td>¥{row.cost?.toLocaleString()}</td>
                          <td>{row.imp.toLocaleString()}</td>
                          <td>{row.click.toLocaleString()}</td>
                          <td className={styles.tdCv} style={{ color: '#00c6ff', fontWeight: 'bold' }}>{row.cv}</td>
                          <td>¥{row.cpa.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </ReportLayout>
  );
};

export default AdAnalysis;
